import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Badge } from '../components/badges';
import { Topbar } from '../components/Shell';
import { Empty, ErrorBox, Loading } from '../components/ui';
import { get } from '../lib/api';
import { fmtAgo, fmtDate, fmtDateTime, STATUS_LABEL } from '../lib/format';
import type { Lineup, MatchData, MatchDataView, MatchEvent } from '../lib/types';

const LIVE = ['FIRST_HALF', 'HALF_TIME', 'SECOND_HALF', 'EXTRA_TIME', 'PENALTIES'];

const EVENT_LABEL: Record<MatchEvent['kind'], string> = {
  goal: 'Goal',
  own_goal: 'Own goal',
  penalty_goal: 'Penalty',
  missed_penalty: 'Missed penalty',
  yellow_card: 'Yellow card',
  second_yellow: 'Second yellow',
  red_card: 'Red card',
  substitution: 'Substitution',
  var: 'VAR',
  period: '',
};

const minuteOf = (e: Pick<MatchEvent, 'minute' | 'addedTime'>) => (e.minute === null ? '' : `${e.minute}${e.addedTime ? `+${e.addedTime}` : ''}'`);

/** What the data feed says about a match: events, lineups, stats, form, head to head. */
export function MatchDataPage() {
  const { matchId } = useParams({ strict: false }) as { matchId: string };
  const id = Number(matchId);
  const q = useQuery({
    queryKey: ['match', id, 'data'],
    queryFn: () => get<MatchDataView>(`/admin/matches/${id}/data`),
    refetchInterval: (query) => (query.state.data && LIVE.includes(query.state.data.match.status) ? 20_000 : false),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBox error={q.error} /></div>;
  const { match: m, data, fromFeed, fetchedAt } = q.data!;
  return (
    <>
      <Topbar
        title={`${m.home.name} v ${m.away.name}`}
        sub={
          <span className="row" style={{ gap: 6 }}>
            <Badge spec={m.competition.badge} size={16} /> {m.competition.name} · {fmtDateTime(m.kickoffAt)}
            {data?.round && <> · Round {data.round}</>}
            {fetchedAt && <span className="muted"> · from the data feed {fmtAgo(fetchedAt)}</span>}
          </span>
        }
        actions={
          <Link to="/matches/$matchId" params={{ matchId: String(id) }} className="btn">
            Control room
          </Link>
        }
      />
      <div className="page stack" style={{ gap: 20 }}>
        <section className="card">
          <div className="card-body">
            <div className="scoreboard">
              <div className="side">
                <Badge spec={m.home.badge} size={64} />
                {m.home.name}
              </div>
              <div className="stack" style={{ alignItems: 'center', gap: 6 }}>
                <span className="big num">{data?.score ? `${data.score[0]} – ${data.score[1]}` : 'v'}</span>
                {data?.penalties && <span className="muted num">Penalties {data.penalties[0]} – {data.penalties[1]}</span>}
                <span className={LIVE.includes(m.status) ? 'pill pill-live' : 'pill'}>{data?.status.clock ?? data?.status.reason ?? STATUS_LABEL[m.status]}</span>
              </div>
              <div className="side">
                <Badge spec={m.away.badge} size={64} />
                {m.away.name}
              </div>
            </div>
          </div>
        </section>
        {!fromFeed ? (
          <Empty>This match was entered by hand, so the data feed has nothing on it.</Empty>
        ) : !data ? (
          <div className="note-box">The data feed isn't answering right now (or isn't configured). Try again in a minute.</div>
        ) : (
          <MatchDataSections d={data} home={m.home.name} away={m.away.name} />
        )}
      </div>
    </>
  );
}

function Card({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <section className="card" aria-label={title}>
      <div className="card-head">
        <h2>{title}</h2>
        {extra}
      </div>
      {children}
    </section>
  );
}

export function MatchDataSections({ d, home, away }: { d: MatchData; home: string; away: string }) {
  const side = (s: 'HOME' | 'AWAY' | null) => (s === 'HOME' ? home : s === 'AWAY' ? away : '');
  return (
    <div className="cols cols-main">
      <div className="stack" style={{ gap: 20 }}>
        {d.events.length > 0 && (
          <Card title="Events">
            <table className="table">
              <tbody>
                {d.events.map((e, i) =>
                  e.kind === 'period' ? (
                    <tr key={i}>
                      <td colSpan={4} className="muted small" style={{ textAlign: 'center', fontWeight: 600 }}>
                        {e.detail} {e.score ? `${e.score[0]} – ${e.score[1]}` : ''}
                      </td>
                    </tr>
                  ) : (
                    <tr key={i}>
                      <td className="num" style={{ width: 56 }}>
                        {minuteOf(e)}
                      </td>
                      <td>
                        <span className={e.kind.endsWith('goal') && e.kind !== 'missed_penalty' ? 'pill pill-green' : e.kind.includes('red') ? 'pill pill-red' : e.kind.includes('yellow') ? 'pill pill-amber' : 'pill'}>
                          {EVENT_LABEL[e.kind]}
                        </span>
                      </td>
                      <td>
                        <strong>{e.player ?? '—'}</strong> {e.detail && <span className="muted small">{e.detail}</span>}
                        <div className="muted small">{side(e.side)}</div>
                      </td>
                      <td className="right num">{e.score ? `${e.score[0]} – ${e.score[1]}` : ''}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </Card>
        )}

        {d.lineups && (
          <Card title={d.lineups.confirmed ? 'Lineups' : 'Predicted lineups'} extra={!d.lineups.confirmed && <span className="pill pill-amber">Not confirmed yet</span>}>
            <div className="cols cols-2" style={{ gap: 0 }}>
              <LineupCol team={home} l={d.lineups.home} />
              <LineupCol team={away} l={d.lineups.away} />
            </div>
          </Card>
        )}

        {d.stats && (
          <Card title="Match stats">
            {d.stats.map((g) => (
              <table key={g.title} className="table">
                <thead>
                  <tr>
                    <th className="right" style={{ width: '25%' }}>
                      {home}
                    </th>
                    <th style={{ textAlign: 'center' }}>{g.title}</th>
                    <th style={{ width: '25%' }}>{away}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((s) => (
                    <tr key={s.label}>
                      <td className="right num">{s.home}</td>
                      <td className="muted" style={{ textAlign: 'center' }}>
                        {s.label}
                      </td>
                      <td className="num">{s.away}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
          </Card>
        )}

        {d.headToHead && (
          <Card title="Head to head">
            <div className="card-body stack">
              <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
                <Kpi label={`${home} wins`} value={d.headToHead.summary.home} />
                <Kpi label="Draws" value={d.headToHead.summary.draws} />
                <Kpi label={`${away} wins`} value={d.headToHead.summary.away} />
              </div>
            </div>
            {d.headToHead.matches.length > 0 && (
              <table className="table">
                <tbody>
                  {d.headToHead.matches.map((h, i) => (
                    <tr key={i}>
                      <td className="muted small" style={{ width: 110 }}>
                        {fmtDate(h.date)}
                      </td>
                      <td className="right">{h.home}</td>
                      <td className="num" style={{ textAlign: 'center', width: 60 }}>
                        <strong>{h.score ?? '–'}</strong>
                      </td>
                      <td>{h.away}</td>
                      <td className="muted small">{h.competition}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}
      </div>

      <div className="stack" style={{ gap: 20 }}>
        <Card title="Match info">
          <div className="card-body">
            <dl className="kv">
              {d.venue && (
                <>
                  <dt>Stadium</dt>
                  <dd>
                    {d.venue.name}
                    {d.venue.city && <span className="muted"> · {d.venue.city}</span>}
                    {d.venue.capacity && <div className="muted small">Capacity {d.venue.capacity.toLocaleString()}{d.venue.surface ? ` · ${d.venue.surface}` : ''}</div>}
                  </dd>
                </>
              )}
              {d.referee && (
                <>
                  <dt>Referee</dt>
                  <dd>
                    {d.referee.name}
                    {d.referee.country && <span className="muted"> · {d.referee.country}</span>}
                  </dd>
                </>
              )}
              {d.attendance !== null && (
                <>
                  <dt>Attendance</dt>
                  <dd className="num">{d.attendance.toLocaleString()}</dd>
                </>
              )}
              {d.weather && (
                <>
                  <dt>Weather</dt>
                  <dd>
                    {d.weather.description}
                    {d.weather.temperature !== null && `, ${Math.round(d.weather.temperature)}°C`}
                    {d.weather.precipitationChance !== null && <span className="muted"> · {d.weather.precipitationChance}% rain</span>}
                  </dd>
                </>
              )}
              {d.playerOfTheMatch && (
                <>
                  <dt>Player of the match</dt>
                  <dd>
                    {d.playerOfTheMatch.name}
                    {d.playerOfTheMatch.rating !== null && <span className="muted"> · {d.playerOfTheMatch.rating}</span>}
                  </dd>
                </>
              )}
            </dl>
          </div>
        </Card>

        {(d.form.home.length > 0 || d.form.away.length > 0) && (
          <Card title="Form">
            <div className="card-body stack">
              {(['home', 'away'] as const).map((k) => (
                <div key={k} className="stack" style={{ gap: 6 }}>
                  <strong className="small">{k === 'home' ? home : away}</strong>
                  <div className="row" style={{ gap: 4 }}>
                    {d.form[k].map((f, i) => (
                      <span
                        key={i}
                        className={f.result === 'W' ? 'pill pill-green' : f.result === 'L' ? 'pill pill-red' : 'pill'}
                        title={`${f.venue === 'H' ? 'v' : '@'} ${f.opponent} ${f.score ?? ''} · ${fmtDate(f.date)}`}
                      >
                        {f.result}
                      </span>
                    ))}
                  </div>
                  <span className="muted small">
                    {d.form[k]
                      .slice(0, 3)
                      .map((f) => `${f.venue === 'H' ? 'v' : '@'} ${f.opponent} ${f.score ?? ''}`)
                      .join(' · ')}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {d.topScorers && (d.topScorers.home || d.topScorers.away) && (
          <Card title="Top scorers">
            <table className="table">
              <tbody>
                {(['home', 'away'] as const).map((k) => {
                  const p = d.topScorers![k];
                  return p ? (
                    <tr key={k}>
                      <td>
                        <strong>{p.name}</strong>
                        <div className="muted small">{k === 'home' ? home : away}</div>
                      </td>
                      <td className="right num">
                        {p.goals ?? 0} goals · {p.assists ?? 0} assists
                        <div className="muted small">
                          {p.games ?? 0} games{p.rating ? ` · rating ${p.rating}` : ''}
                        </div>
                      </td>
                    </tr>
                  ) : null;
                })}
              </tbody>
            </table>
          </Card>
        )}

        {d.insights.length > 0 && (
          <Card title="Insights">
            <ul className="card-body stack" style={{ margin: 0, paddingLeft: 32, gap: 6 }}>
              {d.insights.map((i, n) => (
                <li key={n} className="small">
                  {i.side && <strong>{side(i.side)}: </strong>}
                  {i.text}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <div className="kpi">
      <span className="muted small">{label}</span>
      <strong className="display" style={{ fontSize: 28 }}>
        {value}
      </strong>
    </div>
  );
}

/** "2 goals · assist · yellow · off 62' (injured)" */
function statLine(p: Lineup['starters'][number]): string {
  const s = p.stats;
  if (!s) return '';
  const n = (k: number, one: string) => (k ? (k > 1 ? `${k} ${one}s` : one) : null);
  return [
    n(s.goals, 'goal'),
    n(s.ownGoals, 'own goal'),
    n(s.assists, 'assist'),
    s.redCards ? 'sent off' : s.yellowCards ? 'booked' : null,
    n(s.missedPenalties, 'missed penalty'),
    s.subbedOn !== null ? `on ${s.subbedOn}'` : null,
    s.subbedOff !== null ? `off ${s.subbedOff}'${s.injured ? ' (injured)' : ''}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function LineupCol({ team, l }: { team: string; l: Lineup | null }) {
  if (!l) return <Empty>No lineup for {team} yet.</Empty>;
  return (
    <div style={{ borderTop: '1px solid var(--line-2)' }}>
      <div className="card-body" style={{ paddingBottom: 0 }}>
        <strong>{team}</strong> {l.formation && <span className="pill">{l.formation}</span>}
        {l.coach && (
          <div className="muted small">
            Coach: {l.coach.name}
            {l.coach.country ? ` (${l.coach.country})` : ''}
          </div>
        )}
      </div>
      <table className="table">
        <tbody>
          {[...l.starters, ...l.bench.filter((p) => p.stats?.subbedOn !== null && p.stats?.subbedOn !== undefined)].map((p, i) => (
            <tr key={i}>
              <td className="num muted" style={{ width: 36 }}>
                {p.shirt ?? ''}
              </td>
              <td>
                {p.name} {p.captain && <span className="pill">C</span>} {p.playerOfTheMatch && <span className="pill pill-green">Player of the match</span>}
                <div className="muted small">{statLine(p)}</div>
              </td>
              <td className="muted small">{p.position ?? ''}</td>
              <td className="right num">{p.rating ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {l.bench.length > 0 && (
        <div className="card-body muted small" style={{ paddingTop: 8 }}>
          Bench: {l.bench.filter((p) => !p.stats?.subbedOn).map((p) => p.name).join(', ') || '—'}
        </div>
      )}
      {l.unavailable.length > 0 && (
        <div className="card-body small" style={{ paddingTop: 0 }}>
          <strong>Unavailable</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {l.unavailable.map((u, i) => (
              <li key={i}>
                {u.name}
                <span className="muted">
                  {u.reason ? ` · ${u.reason}` : ''}
                  {u.expectedReturn ? ` · ${u.expectedReturn}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
